import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useHub } from "@/components/AppShell";
import { Field, Loader, PageHeader } from "@/components/common";
import { FormDialog } from "@/components/kids/kidsUi";
import YourData from "@/components/account/YourData";

const RED = "var(--st-red)";
const WHAT = ["Your sign-in, sessions and phone/browser push tokens", "Your Look, private wallpapers, Copilot chats and private memory facts",
  "Your Vault code and connected AI (MCP) apps", "Messages and photos you posted (the files stop working)"];

function DeleteDialog({ info, google, onClose }) {
  const [f, setF] = useState({ confirm: "", password: "" });
  const [err, setErr] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [busy, setBusy] = useState(false);
  const reauth = () => {
    sessionStorage.setItem("qhh.reauth", "delete");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.href.split("#")[0])}`;
  };
  const go = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.post("/me/delete", { confirm: f.confirm, password: info.has_password ? f.password : null, google_session_id: info.has_password ? null : google });
      localStorage.removeItem("qhh.token");
      window.location.replace("/account-deleted");
    } catch (x) {
      const d = x?.response?.data?.detail;
      if (d?.code === "owns_hub") setBlocked(d); else setErr(errMsg(x));
    } finally { setBusy(false); }
  };
  return (
    <FormDialog open onClose={onClose} title="Delete my account" description="This can't be undone." testid="delete-account-dialog">
      {blocked ? (
        <div className="space-y-3" data-testid="delete-blocked">
          <p className="font-semibold text-q-text" data-testid="delete-blocked-msg">{blocked.message}</p>
          {blocked.hubs.map((h) => (
            <div key={h.id} className="flex flex-wrap gap-2">
              <Link to={h.transfer_link} className="q-btn q-btn-secondary min-h-[44px]" data-testid={`blocked-transfer-${h.id}`}>Transfer {h.name}</Link>
              <Link to={h.delete_link} className="q-btn q-btn-ghost min-h-[44px] hover:text-red-300" data-testid={`blocked-delete-hub-${h.id}`}>Delete {h.name}</Link>
            </div>
          ))}
        </div>
      ) : (
        <form onSubmit={go} className="space-y-3">
          <p className="text-sm text-q-muted">We'll delete:</p>
          <ul className="list-disc space-y-1 pl-5 text-sm text-q-text">{WHAT.map((w) => <li key={w}>{w}</li>)}</ul>
          <p className="text-sm text-q-muted">Family records you added (bills, chores, events, recipes, tasks) stay for your family, shown as "Former member". You'll leave every hub.</p>
          <Field label="Type DELETE to confirm"><input className="q-input min-h-[44px] w-full" autoComplete="off" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="delete-confirm-input" /></Field>
          {info.has_password
            ? <Field label="Your password"><input type="password" className="q-input min-h-[44px] w-full" autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="delete-password-input" /></Field>
            : google ? <p className="text-sm font-semibold text-emerald-300" data-testid="delete-google-ok">Google sign-in confirmed</p>
              : <button type="button" className="q-btn q-btn-secondary min-h-[44px] w-full" onClick={reauth} data-testid="delete-google-btn">Confirm with Google</button>}
          {err && <p className="text-sm font-semibold text-red-300" role="alert" data-testid="delete-error">{err}</p>}
          <button className="q-btn min-h-[44px] w-full font-bold text-white" style={{ background: `rgb(${RED})` }} disabled={busy || f.confirm !== "DELETE" || (info.has_password ? !f.password : !google)} data-testid="delete-account-submit">
            {busy ? "Deleting…" : "Delete my account"}
          </button>
        </form>
      )}
    </FormDialog>
  );
}

export default function AccountSettings() {
  const { hub } = useHub();
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [google, setGoogle] = useState(null);
  useEffect(() => {
    const m = window.location.hash.match(/session_id=([^&]+)/);
    if (m && sessionStorage.getItem("qhh.reauth") === "delete") {
      sessionStorage.removeItem("qhh.reauth");
      setGoogle(m[1]); setOpen(true);
      window.history.replaceState(null, "", window.location.pathname);
    }
    api.get("/me/account").then(({ data }) => setInfo(data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  if (!info) return <Loader />;
  return (
    <div data-testid="account-settings-page">
      <PageHeader eyebrow="Settings" title="Account" rgb={RED} testid="account-title" />
      <section className="q-card mb-6 p-4">
        <p className="q-label">Signed in as</p>
        <p className="font-semibold text-q-text" data-testid="account-email">{info.email}</p>
        <p className="text-sm text-q-muted">{info.has_password ? "Email and password" : "Google"}{info.has_password && info.google_linked ? " · Google linked" : ""}</p>
      </section>
      {info.owned_hubs.length > 0 && (
        <section className="q-card mb-6 p-4" data-testid="account-owned-hubs">
          <p className="mb-2 flex items-center gap-2 font-semibold text-q-text"><AlertTriangle className="h-4 w-4 text-amber-300" /> You own {info.owned_hubs.map((h) => h.name).join(", ")}</p>
          <p className="mb-3 text-sm text-q-muted">To delete your account, transfer ownership to a co-parent or delete the hub first.</p>
          {info.owned_hubs.map((h) => (
            <div key={h.id} className="flex flex-wrap gap-2">
              <Link to={h.transfer_link} className="q-btn q-btn-secondary min-h-[44px]" data-testid={`owned-transfer-${h.id}`}>Transfer ownership</Link>
              <Link to={h.delete_link} className="q-btn q-btn-ghost min-h-[44px]" data-testid={`owned-delete-${h.id}`}>Delete {h.name}</Link>
            </div>
          ))}
        </section>
      )}
      {hub && <YourData hub={hub} />}
      <section className="q-card mt-6 p-4" style={{ borderColor: `rgba(${RED},0.4)` }}>
        <h2 className="mb-1 font-display text-lg font-bold text-q-text">Delete my account</h2>
        <p className="mb-3 text-sm text-q-muted">Permanently removes your account from every family on The Queue{hub ? `, including ${hub.name}` : ""}.</p>
        <button className="q-btn q-btn-ghost min-h-[44px] text-red-300" onClick={() => setOpen(true)} data-testid="delete-account-btn"><Trash2 className="h-4 w-4" /> Delete my account</button>
      </section>
      {open && <DeleteDialog info={info} google={google} onClose={() => setOpen(false)} />}
    </div>
  );
}

export function AccountDeleted() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-q-bg p-6" data-testid="account-deleted-page">
      <div className="q-card max-w-md p-8 text-center">
        <h1 className="font-display text-3xl font-extrabold text-q-text" data-testid="account-deleted-msg">Your account has been deleted</h1>
        <p className="mt-3 text-q-muted">Thanks for being part of The Queue.</p>
        <Link to="/" className="q-btn q-btn-primary mt-6 min-h-[44px]" data-testid="account-deleted-home">Back to The Queue</Link>
      </div>
    </main>
  );
}
